import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircleHelp, X } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { useMemberStore } from "@/frontend/stores/memberStore";
import { useProductTourStore, type ProductTourId } from "@/frontend/stores/productTourStore";
import { getTourForPath, PRODUCT_TOURS, type ProductTourStep } from "@/frontend/lib/product-tours";
import { cn } from "@/frontend/lib/utils";
import type { Feature } from "@/shared/rbac";

const EDGE = 16;
const GAP = 12;
const AUTO_START_DELAY_MS = 700;
// Pages render parts of themselves after their data loads.
const AUTO_START_WAIT_MS = 4000;
// A step whose target still isn't on screen after this is skipped, such as
// the sidebar on mobile or a list that is empty.
const STEP_WAIT_MS = 800;

function findTarget(target: string) {
  const element = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  const rect = element?.getBoundingClientRect();
  return element && rect && rect.width > 0 && rect.height > 0 ? { element, rect } : null;
}

function visibleSteps(tourId: ProductTourId, canRead: (feature: Feature) => boolean) {
  return PRODUCT_TOURS[tourId].steps.filter((step) => !step.feature || canRead(step.feature));
}

function placeCard(rect: DOMRect, width: number, height: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clamp = (value: number, max: number) => Math.min(Math.max(value, EDGE), Math.max(EDGE, max));
  if (rect.right + GAP + width <= vw - EDGE) return { left: rect.right + GAP, top: clamp(rect.top, vh - height - EDGE) };
  if (rect.bottom + GAP + height <= vh - EDGE) return { left: clamp(rect.left, vw - width - EDGE), top: rect.bottom + GAP };
  if (rect.top - GAP - height >= EDGE) return { left: clamp(rect.left, vw - width - EDGE), top: rect.top - GAP - height };
  if (rect.left - GAP - width >= EDGE) return { left: rect.left - GAP - width, top: clamp(rect.top, vh - height - EDGE) };
  return { left: (vw - width) / 2, top: vh - height - EDGE };
}

/** Replays a page's walkthrough. Put it next to the page title. */
export function TourHelpButton({ tourId, className }: { tourId: ProductTourId; className?: string }) {
  const { orgId } = useParams<{ orgId: string }>();
  const start = useProductTourStore((state) => state.start);
  const label = `Show the ${PRODUCT_TOURS[tourId].name} walkthrough`;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      className={cn("shrink-0 rounded-full text-muted-foreground hover:text-foreground", className)}
      onClick={() => orgId && start(orgId, tourId)}
    >
      <CircleHelp className="h-[18px] w-[18px]" aria-hidden="true" />
    </Button>
  );
}

/** Starts a page's walkthrough on its first visit and draws whichever walkthrough is open. */
export function ProductTourHost() {
  const { orgId } = useParams<{ orgId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pageTour = orgId ? getTourForPath(pathname, orgId) : null;
  const eligible = useProductTourStore((state) => !!orgId && !!state.eligible[orgId]);
  const seen = useProductTourStore((state) => !!orgId && !!pageTour && (state.seen[orgId] ?? []).includes(pageTour.id));
  const queued = useProductTourStore((state) => !!orgId && !!pageTour && state.queued?.orgId === orgId && state.queued.tourId === pageTour.id);
  const active = useProductTourStore((state) => state.active);
  const firstWinOpen = useProductTourStore((state) => !!orgId && state.firstWin?.orgId === orgId);
  const start = useProductTourStore((state) => state.start);
  const queue = useProductTourStore((state) => state.queue);
  const setStep = useProductTourStore((state) => state.setStep);
  const finish = useProductTourStore((state) => state.finish);
  const fetched = useMemberStore((state) => state.fetched);
  const canRead = useMemberStore((state) => state.canRead);
  const steps = active && active.orgId === orgId ? visibleSteps(active.tourId, canRead) : [];
  const current: ProductTourStep | null = active ? steps[active.step] ?? null : null;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const isOpen = !!current;

  useEffect(() => {
    if (!orgId || !pageTour || active || firstWinOpen || !fetched) return;
    if (!queued && (!eligible || seen)) return;
    if (pageTour.feature && !canRead(pageTour.feature)) return;
    const startedAt = Date.now();
    // Start once something the walkthrough points at has rendered. A page that
    // never shows any of its targets doesn't get a walkthrough.
    const timer = window.setInterval(() => {
      const waited = Date.now() - startedAt;
      if (waited < AUTO_START_DELAY_MS) return;
      const ready = visibleSteps(pageTour.id, canRead).some((step) => findTarget(step.target));
      if (!ready && waited < AUTO_START_WAIT_MS) return;
      window.clearInterval(timer);
      if (ready) start(orgId, pageTour.id);
    }, 200);
    return () => window.clearInterval(timer);
  }, [orgId, pageTour, eligible, seen, queued, active, firstWinOpen, fetched, canRead, start]);

  // Leaving the page (for example with the browser's back button) closes its walkthrough.
  useEffect(() => {
    if (active && (active.orgId !== orgId || pageTour?.id !== active.tourId)) finish();
  }, [active, orgId, pageTour, finish]);

  // Return focus to whatever opened the walkthrough, usually its help button.
  useEffect(() => {
    if (!isOpen) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus({ preventScroll: true });
    };
  }, [isOpen]);

  // Geometry is written straight to the DOM every frame, so the card follows
  // its target through scrolling, resizing and late-loading content.
  useEffect(() => {
    if (!current || !active) return;
    const { step, direction } = active;
    const shownAt = performance.now();
    let scrolled = false;
    let focused = false;
    let frame = 0;
    const tick = () => {
      const wrapper = wrapperRef.current;
      const spotlight = spotlightRef.current;
      const card = cardRef.current;
      if (!wrapper || !spotlight || !card) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const found = findTarget(current.target);
      if (!found) {
        if (performance.now() - shownAt < STEP_WAIT_MS) {
          // Nothing is shown while waiting, so the page stays usable.
          wrapper.style.visibility = "hidden";
          frame = requestAnimationFrame(tick);
          return;
        }
        const next = step + direction;
        if (next >= 0 && next < steps.length) setStep(next, direction);
        else if (direction === -1 && step + 1 < steps.length) setStep(step + 1, 1);
        else finish();
        return;
      }
      wrapper.style.visibility = "visible";
      if (!scrolled) {
        scrolled = true;
        found.element.scrollIntoView({ block: "nearest" });
      }
      if (!focused) {
        focused = true;
        card.focus({ preventScroll: true });
      }
      const { rect } = found;
      Object.assign(spotlight.style, { left: `${rect.left - 6}px`, top: `${rect.top - 6}px`, width: `${rect.width + 12}px`, height: `${rect.height + 12}px` });
      const position = placeCard(rect, card.offsetWidth, card.offsetHeight);
      Object.assign(card.style, { left: `${position.left}px`, top: `${position.top}px` });
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [current, active, steps.length, setStep, finish]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        finish();
        return;
      }
      // Keep keyboard focus inside the card while the page behind is dimmed.
      const card = cardRef.current;
      if (event.key !== "Tab" || !card) return;
      const focusable = [...card.querySelectorAll<HTMLElement>("button:not([disabled])")];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = card.contains(document.activeElement);
      if (event.shiftKey && (!inside || document.activeElement === first || document.activeElement === card)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, finish]);

  if (!active || !current || !orgId) return null;
  const tour = PRODUCT_TOURS[active.tourId];
  const { step } = active;
  // Count only what this page shows, so the numbers match what the person can see.
  const shown = steps.filter((item, index) => index === step || !!findTarget(item.target));
  const position = shown.indexOf(current);
  const isLast = position === shown.length - 1;
  const nextTour = tour.next ? PRODUCT_TOURS[tour.next] : null;
  const canOpenNext = !!nextTour && (!nextTour.feature || canRead(nextTour.feature));

  const goForward = () => {
    if (!isLast) return setStep(steps.indexOf(shown[position + 1]), 1);
    finish();
    if (canOpenNext && nextTour) {
      queue(orgId, nextTour.id);
      navigate(`/org/${orgId}/app/${nextTour.path}`);
    }
  };

  return createPortal(
    <div ref={wrapperRef} className="fixed inset-0 z-[60]" style={{ visibility: "hidden" }}>
      <div
        ref={spotlightRef}
        aria-hidden="true"
        className="pointer-events-none fixed rounded-xl ring-2 ring-emerald-400 shadow-[0_0_0_9999px_rgba(10,20,15,0.55)]"
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-tour-title"
        aria-describedby="product-tour-body"
        tabIndex={-1}
        className="fixed w-[min(320px,calc(100vw-32px))] rounded-2xl border border-border bg-popover p-5 text-popover-foreground shadow-xl outline-none"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
            {position + 1} of {shown.length}
          </p>
          <button
            type="button"
            onClick={finish}
            aria-label="Close walkthrough"
            className="-mr-1 -mt-1 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <h2 id="product-tour-title" className="mt-2 text-base font-semibold tracking-tight">{current.title}</h2>
        <p id="product-tour-body" className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{current.body}</p>
        <div className="mt-4 flex items-center gap-1" aria-hidden="true">
          {shown.map((item, index) => (
            <span key={item.target} className={`h-1.5 rounded-full transition-all ${index === position ? "w-4 bg-emerald-600 dark:bg-emerald-400" : "w-1.5 bg-muted-foreground/25"}`} />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          {isLast ? <span /> : (
            <Button type="button" variant="ghost" size="sm" className="-ml-2 text-muted-foreground" onClick={finish}>
              Skip
            </Button>
          )}
          <div className="flex gap-2">
            {position > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={() => setStep(steps.indexOf(shown[position - 1]), -1)}>
                Back
              </Button>
            )}
            <Button type="button" size="sm" onClick={goForward}>
              {!isLast ? "Next" : canOpenNext && nextTour ? `Show me ${nextTour.name}` : "Got it"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
